import { ReactNode, useCallback, useMemo, useState } from "react";
import { ScheduleLifecycleVerb } from "@common/types/domains/accounting/schedule";
import { ScheduleApplicationBulkLifecycleOption } from "@components/commands/lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import {
    getSingleScheduleEntityId,
    getUniqueScheduleApplicationIds,
} from "./scheduleApplicationRowId";
import {
    SCHEDULE_BULK_LIFECYCLE_KEYWORDS,
    SCHEDULE_BULK_LIFECYCLE_MENU_LABELS,
    SCHEDULE_BULK_LIFECYCLE_VERBS,
    ScheduleBulkLifecycleVerb,
    formatScheduleBulkLifecycleBody,
    formatScheduleBulkLifecycleTitle,
    formatScheduleBulkSkippedWarning,
} from "./scheduleLifecycleBulkCopy";
import { SCHEDULE_STOP_BODY } from "./scheduleLifecycleCopy";
import {
    ScheduleBulkEntityGroup,
    ScheduleStopBulkTarget,
    buildScheduleStopBulkTarget,
    getScheduleBulkLifecycleDisabledReason,
    groupScheduleRowsByEntity,
    partitionScheduleRowsForLifecycle,
} from "./scheduleLifecycleBulkHelpers";
import {
    ScheduleBulkConfirmTarget,
    ScheduleLifecycleBulkConfirmModal,
} from "./ScheduleLifecycleBulkConfirmModal";
import { ScheduleStopModal } from "./ScheduleStopModal";
import { useScheduleLifecycleBulkSubmit } from "./useScheduleLifecycleBulkSubmit";

export function useScheduleApplicationsBulkLifecycle(options: {
    selectedRows: ScheduleApplicationParentRow[];
    onCompleted: (failedApplicationIds: string[]) => void;
}): {
    actions: ScheduleApplicationBulkLifecycleOption[];
    modals: ReactNode;
} {
    const { selectedRows, onCompleted } = options;
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [confirmTarget, setConfirmTarget] =
        useState<ScheduleBulkConfirmTarget | null>(null);
    const [stopTarget, setStopTarget] = useState<
        (ScheduleStopBulkTarget & { note?: string }) | null
    >(null);
    const [pendingSubmission, setPendingSubmission] = useState<{
        groups: ScheduleBulkEntityGroup[];
        skippedApplicationIds: string[];
    }>({ groups: [], skippedApplicationIds: [] });
    const [isStopShown, setIsStopShown] = useState(false);
    const close = useCallback(() => {
        setConfirmTarget(null);
        setIsStopShown(false);
    }, []);
    const { submit, isPending } = useScheduleLifecycleBulkSubmit({
        onClose: close,
        onCompleted,
    });

    const open = useCallback(
        (verb: ScheduleBulkLifecycleVerb) => {
            if (selectedRows.length === 0 || refuseIfViewOnly()) {
                return;
            }
            const { eligible, skipped } = partitionScheduleRowsForLifecycle({
                verb,
                rows: selectedRows,
            });
            if (eligible.length === 0) {
                return;
            }
            const skippedWarning = formatScheduleBulkSkippedWarning({
                skipped,
                selectedCount:
                    getUniqueScheduleApplicationIds(selectedRows).length,
            });
            setPendingSubmission({
                groups: groupScheduleRowsByEntity(eligible),
                skippedApplicationIds: skipped.map((row) => row.applicationId),
            });
            if (verb === ScheduleLifecycleVerb.cancel) {
                setStopTarget({
                    ...buildScheduleStopBulkTarget(eligible),
                    note: skippedWarning,
                });
                setIsStopShown(true);
                return;
            }
            setConfirmTarget({
                verb,
                count: getUniqueScheduleApplicationIds(eligible).length,
                entityId: getSingleScheduleEntityId(eligible),
                body: formatScheduleBulkLifecycleBody({ verb, rows: eligible }),
                hasRatableSchedule: eligible.some((row) => !row.isMilestone),
                skippedWarning,
            });
        },
        [refuseIfViewOnly, selectedRows],
    );

    const actions = useMemo(
        () =>
            SCHEDULE_BULK_LIFECYCLE_VERBS.map(
                (verb): ScheduleApplicationBulkLifecycleOption => ({
                    id: `schedule-bulk-${verb}`,
                    label: SCHEDULE_BULK_LIFECYCLE_MENU_LABELS[verb],
                    keywords: SCHEDULE_BULK_LIFECYCLE_KEYWORDS[verb],
                    disabledReason: getScheduleBulkLifecycleDisabledReason({
                        verb,
                        rows: selectedRows,
                    }),
                    onSelect: () => open(verb),
                }),
            ),
        [open, selectedRows],
    );

    return {
        actions,
        modals: (
            <>
                <ScheduleLifecycleBulkConfirmModal
                    target={confirmTarget}
                    onHide={close}
                    onConfirm={(confirmation) =>
                        submit({ ...pendingSubmission, ...confirmation })
                    }
                />
                <ScheduleStopModal
                    show={isStopShown}
                    content={
                        stopTarget
                            ? {
                                  title: formatScheduleBulkLifecycleTitle({
                                      verb: ScheduleLifecycleVerb.cancel,
                                      count: stopTarget.count,
                                  }),
                                  body: SCHEDULE_STOP_BODY,
                                  note: stopTarget.note,
                                  summary: stopTarget.summary,
                                  entityId: stopTarget.entityId,
                              }
                            : null
                    }
                    isSubmitting={isPending}
                    onHide={close}
                    onExited={() => setStopTarget(null)}
                    onConfirm={(settlement) =>
                        submit({
                            verb: ScheduleLifecycleVerb.cancel,
                            ...pendingSubmission,
                            settlement,
                        })
                    }
                />
            </>
        ),
    };
}
